import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { CnImageViewer, CnIcon, CnLoading, CnForm, formilyCore, CnBalloon, CnRadioGroup,
  CnMessage, CnBox,
 } from '@sto/cn-ui';
import styles from './index.module.scss';
import ImageUploader from './uploadImg';
import { getSearchList, uploadAndGetOssFile } from '@/services/fetchs';
import FastQuestion from './fastQuestion';
import { useChatContext } from '@/store/chatContext';
import { debounce } from 'lodash';
import { getItemByValue } from '@/utils/tools';
import { focusToEnd, focusToStart, setContentAndFocusToEnd } from '@/utils/editorFocus';
import { useChatStore } from '@/store';
import { TimePicker, CustomRadioGroup, CustomCheckBox } from '@/components';
import moment from 'moment';
import mockData from './1.json';

const { createForm, onFormValuesChange } = formilyCore;

// eslint-disable-next-line react/prop-types
const RichInput = ({ onSubmit, robotCode }) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [fileUrls, setFileUrls] = useState<string[]>([]);
  const [fileTypes, setFileTypes] = useState<string[]>([]); // 存储文件类型：'image' 或 'file'
  const [fileNames, setFileNames] = useState<string[]>([]); // 存储文件名
  const [fileSizes, setFileSizes] = useState<string[]>([]); // 存储文件大小
  const [isEmpty, setIsEmpty] = useState(true); // 用于判断输入框是否为空
  const editorRef = useRef<HTMLDivElement>(null);
  const { isDataAnalysisMode } = useChatContext();
  const { isNewChat, activeHistoryId, quickFormInfo, setQuickFormInfo, botVisible } = useChatStore();
  const [associateVisible, setAssociateVisible] = useState(false); // 联想浮框是否展示
  const [associateList, setAssociateList] = useState([]); // 联想列表
  const [quickInfo, setQuickInfo] = useState<any>(); // 快捷输入信息
  const [checkedTag, setCheckedTag] = useState(); // 选中的标签
  const [streamOtherParam, setStreamOtherParam] = useState<{ actionParam?: any; [key: string]: any }>({}); // 流式接口的其他入参
  const arrTemplate = useRef([]); // 快捷输入表单的模版字符串
  const [questionTemplate, setQuestionTemplate] = useState(''); // 问题模版字符串

  // 输入框自动聚焦
  useEffect(() => {
    if (editorRef.current && botVisible) {
      handleFocusToEnd();
    } else {
      handleFocusToStart();
      resetQuickInput();
    }
  }, [botVisible]);

  // 新会话或者历史会话，聚焦到编辑器内容末尾并重置快捷输入表单
  useEffect(() => {
    if (isNewChat || activeHistoryId) {
      handleFocusToEnd();
      resetQuickInput();
    }
  }, [isNewChat, activeHistoryId]);

  // 快捷输入表单，处理快捷输入表单的值
  const quickInputForm = useMemo(
    () =>
      createForm({
        effects: () => {
          onFormValuesChange((form) => {
            const values = form.values;
            const keys = Object.keys(values)?.filter(key => {
              if (!questionTemplate) {
                // 没有问题模板的，过滤掉空值
                return values[key];
              }
              return true;
            });
            if (keys?.length) {
              let content = getEditorContent();
              let copyContent = content;
              if (content) {
                copyContent = content + ' ';
              }
              const resetValues = {};
              keys?.forEach(key => {
                const currentValue = values[key];
                if (key.includes('Info')) {
                  return;
                }
                if (key.toLocaleLowerCase().includes('org')) {
                  // 组织选择器：处理组织选择的值；组织选择器不重置，直接使用值
                  form.query(key).take((queryField) => {
                    const dataSource = queryField?.componentProps?.dataSource;
                    const orgInfo = getItemByValue(currentValue, dataSource);
                    if (questionTemplate) {
                      const index = questionTemplate.split(' ').findIndex(item => item.includes(`$${key}`));
                      if (currentValue) {
                        // 替换后端的模版字符串变量
                        arrTemplate.current[index] = `#${orgInfo?.label}`;
                        content = arrTemplate.current?.filter(item => !item.includes('$')).join(' ');
                        setStreamOtherParam(prev => ({
                          ...prev,
                          actionParam: {
                            ...(prev.actionParam || {}),
                            targetOrgCode: orgInfo?.value, // 组织编码
                            targetOrgName: orgInfo?.label, // 组织名称
                            authLevel: orgInfo?.authLevel, // 组织层级
                          },
                        }));
                      } else {
                        // 置空
                        arrTemplate.current[index] = `$${key}`;
                        const templateContentArr = arrTemplate.current?.filter(item => !item.includes('$'));
                        if (templateContentArr?.length && templateContentArr?.length > 1) {
                          content = templateContentArr?.join(' ');
                        } else {
                          content = '';
                        }
                        setStreamOtherParam(prev => ({
                          ...prev,
                          actionParam: {
                            ...(prev.actionParam || {}),
                            targetOrgCode: '', // 组织编码
                            targetOrgName: '', // 组织名称
                            authLevel: '', // 组织层级
                          },
                        }));
                      }
                    } else {
                      const newContent = '#' + orgInfo?.label + ' ';
                      const contentExist = copyContent.includes(newContent);
                      if (orgInfo && !contentExist) {
                        content += ' ' + newContent;
                        resetValues[key] = orgInfo?.value;
                        resetValues[key + 'Info'] = orgInfo;
                      }
                      // form.reset();
                    }
                  });
                } else if (key.toLocaleLowerCase().includes('date') || ['day', 'week', 'month', 'quarter', 'year', 'mtd'].includes(key.toLocaleLowerCase())) {
                  // 日期选择器
                  if (questionTemplate) {
                    const index = questionTemplate?.split(' ').findIndex(item => item.includes(`$${key}`));
                    if (currentValue) {
                      const { dimension, range, value } = currentValue;
                      // 替换后端的模版字符串变量
                      arrTemplate.current[index] = `#${value}`;
                      content = arrTemplate.current?.filter(item => !item.includes('$')).join(' ');
                      setStreamOtherParam(prev => ({
                        ...prev,
                        actionParam: {
                          ...(prev.actionParam || {}),
                          statDate: moment(value).format('YYYYMMDD'), // 业务日期
                          dateDimension: dimension, // 日期维度：DAY、WEEK、MONTH、QUARTER、YEAR
                        },
                      }));
                    } else {
                      // 置空
                      arrTemplate.current[index] = `$${key}`;
                      const templateContentArr = arrTemplate.current?.filter(item => !item.includes('$'));
                      if (templateContentArr?.length && templateContentArr?.length > 1) {
                        content = templateContentArr?.join(' ');
                      } else {
                        content = '';
                      }
                      setStreamOtherParam(prev => ({
                        ...prev,
                        actionParam: {
                          ...(prev.actionParam || {}),
                          statDate: '', // 业务日期
                          dateDimension: '', // 日期维度：DAY、WEEK、MONTH、QUARTER、YEAR
                        },
                      }));
                    }
                  } else {
                    // 日期选择器（快捷指标查询）
                    resetValues[key] = currentValue;
                    let newContent = '';
                    if (Array.isArray(currentValue)) {
                      newContent = '#' + currentValue?.join('-') + ' ';
                    } else if (typeof currentValue === 'object') {
                      newContent = '#' + currentValue?.dimensionText + currentValue?.value + ' ';
                    } else {
                      newContent = '#' + currentValue + ' ';
                    }
                    const contentExist = copyContent.includes(newContent);
                    if (!contentExist) {
                      content += ' ' + newContent;
                    }
                    form.reset();
                  }
                } else {
                  if (questionTemplate) {
                    // 问题模版字符串
                    const index = questionTemplate?.split(' ').findIndex(item => item.includes(`$${key}`));
                    if (currentValue) {
                      arrTemplate.current[index] = `#${currentValue}`;
                      content = arrTemplate.current?.filter(item => !item.includes('$')).join(' ');
                    } else {
                      arrTemplate.current[index] = `$${key}`;
                      const templateContentArr = arrTemplate.current?.filter(item => !item.includes('$'));
                      if (templateContentArr?.length && templateContentArr?.length > 1) {
                        content = templateContentArr?.join(' ');
                      } else {
                        content = '';
                      }
                    }
                  } else {
                    // 其他字段（如单选组）
                    resetValues[key] = currentValue;
                    const newContent = '#' + currentValue + ' ';
                    const contentExist = copyContent.includes(newContent);
                    if (!contentExist) {
                      content += ' ' + newContent;
                    }
                    form.reset();                    
                  }
                }
              });
              setEditorContent(content);
            }
          })
        },
      }),
    [quickInfo, quickFormInfo, checkedTag, questionTemplate],
  );

  // 防抖获取联想列表
  const getSearchListDebounce = debounce(() => {
    const content = getEditorContent();
    if (!content) return;
    getSearchList({
      queryText: content,
    }).then((res) => {
      if (res.success) {
        if (res.data.length) {
          const content = getEditorContent();
          if (content) {
            setAssociateVisible(true);
            setAssociateList(res.data);
          } else {
            setAssociateVisible(false);
            setAssociateList([]);
          }
        } else {
          setAssociateVisible(false);
          setAssociateList([]);
        }
      } else {
        setAssociateVisible(false);
        setAssociateList([]);
        CnMessage.error(res.errMessage);
      }
    })
  }, 300);

  // 聚焦到编辑器内容末尾的方法
  const handleFocusToEnd = useCallback(() => {
    focusToEnd(editorRef.current);
  }, []);

  // 聚焦到编辑器开头的方法
  const handleFocusToStart = useCallback(() => {
    focusToStart(editorRef.current);
  }, []);

  // 检查输入框是否为空，并更新状态
  const checkIfEmpty = useCallback(() => {
    const content = getEditorContent();
    if (content === '') {
      setIsEmpty(true);
      setAssociateVisible(false);
    } else {
      setIsEmpty(false);
      getSearchListDebounce();
    }
  }, []);

  // 格式化文件大小
  const formatFileSize = useCallback((bytes: number) => {
    if (bytes < 1024) {
      return `${bytes}B`;
    } else if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)}KB`;
    } else {
      return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
    }
  }, []);

  // 根据文件URL获取对应的图标
  const getFileIcon = useCallback((url: string) => {
    const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
    const iconMap: Record<string, string> = {
      'ppt': 'https://imgcdn.sto.cn/82963C8E-2AC4-40-200x200-ppt.png',
      'pptx': 'https://imgcdn.sto.cn/82963C8E-2AC4-40-200x200-ppt.png',
      'doc': 'https://imgcdn.sto.cn/775EC0C7-4396-4E-200x200-WORD.png',
      'docx': 'https://imgcdn.sto.cn/775EC0C7-4396-4E-200x200-WORD.png',
      'xls': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
      'xlsx': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
      'csv': 'https://imgcdn.sto.cn/BD3490FA-C079-4B-200x160-excel-.png',
    };
    return iconMap[ext] || 'https://imgcdn.sto.cn/97F4A4A4-BDB9-4A-200x200-wenjian.png';
  }, []);

  // 上传文件（支持图片和其他类型文件）
  const uploadFile = useCallback(async (file) => {
    let index;
    const isImage = file.type.startsWith('image/'); // 判断是否为图片
    const fileType = isImage ? 'image' : 'file';
    const fileName = file.name || '未命名文件';
    const fileSize = formatFileSize(file.size);
    
    setFileUrls((v) => {
      index = v.length;
      return [...v, 'loading'];
    });
    setFileTypes((v) => [...v, fileType]);
    setFileNames((v) => [...v, fileName]);
    setFileSizes((v) => [...v, fileSize]);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bizType', 'DING_CHAT_RECORD');
    const {
      data: { tempUrl },
    } = await uploadAndGetOssFile(formData);
    tempUrl &&
      setFileUrls((v) => {
        const urls = [...v];
        if (urls[index]) {
          urls[index] = tempUrl
        }
        index = undefined;
        return [...urls];
      });
  }, [formatFileSize]);

  // 处理粘贴事件
  const handlePaste = useCallback(
    (e) => {
      e.preventDefault(); // 阻止默认的粘贴行为
      const clipboardData = e.clipboardData || window.Clipboard;
      const clipboardItems = clipboardData.items;
      
      // 遍历剪贴板项，查找文件
      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];
        // 检查是否是文件类型（图片或其他文件）
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          if (blob) {
            uploadFile(blob);
            return;
          }
        }
      }
      
      // 如果没有文件，处理纯文本
      let pastedData = clipboardData.getData('text/plain'); // 只获取纯文本
      // 去除末尾的换行
      pastedData = pastedData.replace(/\n+$/, '');
      // 将 &nbsp; 替换为空格
      pastedData = pastedData?.replace(/&amp;/g, '&')?.replace(/&nbsp;/g, ' ');
      // 插入纯文本到光标位置
      document.execCommand('insertText', false, pastedData);
    },
    [uploadFile],
  );

  // 提交处理（发送问题）
  const handleSubmit = useCallback(
    (e) => {
      setQuickInfo(null);
      setAssociateVisible(false);
      e.preventDefault();
      // 在这里处理提交的内容，例如将内容发送到服务器
      const content = getEditorContent()
        ?.replace(/&amp;/g, '&')
        ?.replace(/&nbsp;/g, ' ');
      if (editorRef.current) {
        editorRef.current.textContent = '';
      }
      
      // 根据文件类型确定 messageTypeEnum
      let messageTypeEnum;
      if (fileUrls?.length > 0) {
        // 如果有任何非图片文件，则为 'file'，否则为 'picture'
        const hasNonImage = fileTypes.some(type => type === 'file');
        messageTypeEnum = hasNonImage ? 'file' : 'picture';
      }
      
      setFileUrls([]);
      setFileTypes([]);
      setFileNames([]);
      setFileSizes([]);
      checkIfEmpty();
      onSubmit({
        content,
        fileUrls,
        ...(messageTypeEnum ? { messageTypeEnum } : {}),
        ...(isDataAnalysisMode ? { skillType: 'DATA_ANALYSIS' } : {})
      }, streamOtherParam);
      setStreamOtherParam({});
      resetQuickInput();
    },
    [checkIfEmpty, fileUrls, fileTypes, onSubmit, isDataAnalysisMode, streamOtherParam],
  );

  // 处理键盘事件
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        if (e.ctrlKey || e.metaKey) {
          // Ctrl + Enter 或者 command + 回车 换行
          document.execCommand('insertLineBreak');
          e.preventDefault();
        } else {
          // Enter 发送内容
          handleSubmit(e);
        }
      }
    },
    [handleSubmit],
  );

  // 删除文件
  const onDelete = useCallback((url) => {
    setFileUrls((v) => {
      const index = v.findIndex((fileUrl) => fileUrl === url);
      if (index !== -1) {
        // 同步删除对应的文件类型、文件名和文件大小
        setFileTypes((types) => types.filter((_, i) => i !== index));
        setFileNames((names) => names.filter((_, i) => i !== index));
        setFileSizes((sizes) => sizes.filter((_, i) => i !== index));
        return v.filter((_, i) => i !== index);
      }
      return v;
    });
  }, []);

  // 使用 useEffect 在每次内容更改后检查输入框状态
  useEffect(checkIfEmpty, [checkIfEmpty]);

  // 组件挂载时自动聚焦到编辑器
  useEffect(() => {
    // 延迟聚焦，确保DOM已经渲染完成
    const timer = setTimeout(() => {
      handleFocusToEnd();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [handleFocusToEnd]);

  // 设置编辑器内容的方法
  const setEditorContent = useCallback((content: string) => {
    if (editorRef.current) {
      setContentAndFocusToEnd(editorRef.current, content);
      checkIfEmpty();
    }
  }, [checkIfEmpty]);

  // 获取编辑器内容的方法
  const getEditorContent = useCallback(() => {
    return editorRef.current?.textContent?.trim?.() || '';
  }, []);

  // 快捷输入区域的表单内容
  const QuickInputForm = useMemo(() => {
    if ((!quickInfo || !checkedTag) && !quickFormInfo) return null;
    let schema = {};
    if (quickFormInfo) {
      // 【猜您还想咨询以下问题】的快捷表单信息
      const { schema: schemaStr, questionTemplate, taskType } = quickFormInfo;
      schema = JSON.parse(schemaStr);
      arrTemplate.current = questionTemplate?.split(' ');
      setQuestionTemplate(questionTemplate);
      setStreamOtherParam(prev => ({
        ...prev,
        taskType,
      }));
    } else {
      if (!quickInfo || !checkedTag) return null;
      const matchItem = (quickInfo?.questionList || []).find((item: any) => item?.taskType === checkedTag) || {};
      const { keyWordList, questionTemplate } = matchItem;
      schema = JSON.parse(keyWordList);
      arrTemplate.current = questionTemplate?.split(' ');
      setQuestionTemplate(questionTemplate);
    }
    // schema = mockData;
    if (!schema) return null;
    // console.log(schema, 'schema');
    
    return (
      <CnForm
        key={quickFormInfo ? `quickFormInfo-${quickFormInfo?.taskType}` : `${(quickInfo as any)?.content || ''}-${checkedTag}`} // 添加key强制重新渲染
        form={quickInputForm}
        formLayoutProps={{
          labelCol: {
            fixedSpan: 0,
          },
        }}
        schema={schema}
        components={{
          TimePicker,
          CustomRadioGroup,
          CustomCheckBox,
        }}
      />
    )
  }, [quickInfo, checkedTag, quickInputForm, quickFormInfo]);

  // 点击快速问题标签，设置快捷输入信息
  const onClickFastQuestionTag = useCallback((info) => {
    setQuickFormInfo(null);
    setQuickInfo(info);
    const taskType = info?.questionList?.[0]?.taskType || null;
    setCheckedTag(taskType);
    setStreamOtherParam({
      taskType: taskType,
    });
  }, []);

  // 重置快捷输入区域
  const resetQuickInput = useCallback(() => {
    setQuickFormInfo(null); // 重置答案区触发的快捷表单信息
    setQuickInfo(null);
    setCheckedTag(null);
    setAssociateVisible(false);
    handleFocusToEnd();
  }, []);

  /**
   * 渲染快捷输入内容区域
   * @returns 
   */
  const renderQuickInput = () => {
    const info = quickFormInfo || quickInfo;
    if (!info || !QuickInputForm) return null;
    const { questionList, title, content } = info;
    const boxTitle = title || content;
    
    return (
      <React.Fragment>
        <div className={styles.quickInputBox}>
          <CnBox direction="row" className={styles.quickInputHeader} justify="space-between" align="center">
            <CnBox align="center" direction="row" className={styles.quickInputHeaderTitleBox}>
              <img src={'https://imgcdn.sto.cn/ACA23615-6672-40-16x16-tag-icon.png'} alt="" />
              <span className={styles.quickInputHeaderTitle}>{boxTitle}</span>
            </CnBox>
            <img 
              src={'https://imgcdn.sto.cn/F80F6EF8-C5F7-46-18x18-error-icon.png'} 
              alt="" 
              style={{ cursor: 'pointer' }} 
              onClick={resetQuickInput}
            />
          </CnBox>
          {questionList?.length > 1 ? (
            <React.Fragment>
              <div className={styles.quickInputRadioGroupBox}>
                <CnRadioGroup 
                  value={checkedTag}
                  shape="tag"
                  dataSource={questionList?.map(item => ({ label: item.question, value: item.taskType }))}
                  onChange={(value) => {
                    setCheckedTag(value);
                    setStreamOtherParam(prev => ({
                      ...prev,
                      taskType: value,
                    }));
                  }}
                  size="large"
                />
              </div>
              <div className={styles.divider} />
              <div className={styles.quickInputTitle}>常用关键词</div>
            </React.Fragment>
          ) : (
            questionList?.length === 1 ? (
              <React.Fragment>
                <div className={styles.quickInputTitle}>{questionList[0]?.question}</div>
              </React.Fragment>
            ) : null
          )}
          {QuickInputForm}
        </div>
        {info && <div className={styles.divider} />}
      </React.Fragment>
    );
  };

  const renderPlaceholder = useMemo(() => {
    switch (checkedTag) {
      case 'SUBSCRIBE':
        return (
          <span>
            快把想订阅的内容告诉我吧~
            <br />
            例如：帮我订阅【网点发件量达成监控】
          </span>
        );
      default:
        return '请输入问题，可Ctrl+V粘贴图片或文件，Enter键快捷发送';
    }
  }, [checkedTag]);

  return (
    <div className={styles.richInput} style={{ width: '100%' }}>
      {/* 指标按钮组 */}
      <FastQuestion robotCode={robotCode} onSubmit={onSubmit} onClickFastQuestionTag={onClickFastQuestionTag} isShow={!quickInfo && !quickFormInfo}/>

      <CnBalloon
        visible={associateVisible}
        trigger={
          <div className={styles.inputBox}>
            <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
              {!!fileUrls?.length ? (
                <div className={styles.imgList}>
                  {fileUrls?.map((url, index) =>
                    url === 'loading' ? (
                      <CnLoading tip=" " inline={false} key={index}>
                        <div
                          className={styles.img}
                          style={{ width: 45, height: 45 }}
                        />
                      </CnLoading>
                    ) : (
                      <div 
                        key={index} 
                        className={styles.fileCard}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 8px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px',
                          position: 'relative',
                          maxWidth: '160px',
                        }}
                        onMouseEnter={(e) => {
                          const closeBtn = e.currentTarget.querySelector('.file-close-btn') as HTMLElement;
                          if (closeBtn) closeBtn.style.display = 'flex';
                        }}
                        onMouseLeave={(e) => {
                          const closeBtn = e.currentTarget.querySelector('.file-close-btn') as HTMLElement;
                          if (closeBtn) closeBtn.style.display = 'none';
                        }}
                      >
                        <div 
                          className="file-close-btn"
                          onClick={() => onDelete(url)}
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                          }}
                        >
                          <CnIcon
                            type="close"
                            size="xxs"
                            style={{ color: '#fff' }}
                          />
                        </div>
                        {fileTypes[index] === 'image' ? (
                          <CnImageViewer
                            style={{ zIndex: 1, flexShrink: 0 }}
                            width={36}
                            height={36}
                            src={url}
                          />
                        ) : (
                          <img 
                            src={getFileIcon(url)}
                            alt="file"
                            style={{ 
                              width: 36, 
                              height: 36, 
                              objectFit: 'cover',
                              borderRadius: 4,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <div style={{
                          flex: 1,
                          overflow: 'hidden',
                          minWidth: 0,
                        }}>
                          <div style={{
                            fontSize: '11px',
                            color: '#333',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: 500,
                            lineHeight: '14px',
                          }}>
                            {fileNames[index]}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            color: '#999',
                            marginTop: '2px',
                            lineHeight: '12px',
                          }}>
                            {fileTypes[index] === 'image' ? 'Image' : 'File'} · {fileSizes[index]}
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : null }
              {/* 快捷输入区域 */}
              {renderQuickInput()}
              {/* 聊天输入框 */}
              <div style={{ position: 'relative', padding: '12px' }}>
                <div
                  ref={editorRef}
                  contentEditable={!quickFormInfo}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onInput={checkIfEmpty}
                  className={`${styles.input} ${
                    fileUrls?.length ? styles.hasMsg : ''
                  }`}
                  style={quickFormInfo ? { cursor: 'not-allowed' } : {}}
                />
                {isEmpty && !quickFormInfo ? (
                  <div className={styles.placeholder}>
                    {/* 20250826 甜甜说：要特殊处理订阅按钮的placeholder */}
                    {renderPlaceholder}
                  </div>
                ) : null}
              </div>
            </form>
            <div className={styles.submit}>
              <ImageUploader submit={(url, fileType, fileName, fileSize) => {
                setFileUrls((v) => [...v, url]);
                setFileTypes((v) => [...v, fileType]);
                setFileNames((v) => [...v, fileName]);
                setFileSizes((v) => [...v, fileSize]);
              }} />
              <span className={styles.submitBtn} onClick={handleSubmit}>
                发送
              </span>
            </div>
          </div>
        }
        title=""
        align="t"
        popupClassName={styles.associatePopup}
        needAdjust={false}
        delay={0}
        v2
        followTrigger
        // onVisibleChange={(flag) => setAssociateVisible(flag)}
        safeNode={[formRef.current]}
      >
        <div className={styles.associateListBox}>
          {associateList.map((item) => (
            <div 
              key={item.recommend} 
              className={styles.associateItem} 
              onClick={() => {
                setAssociateVisible(false);
                setEditorContent(item.recommend);
              }}
            >
              <img src={'https://imgcdn.sto.cn/2DACA503-F5CB-48-18x14-star-icon.png'} alt="" />
              <span>{item.recommend}</span>
            </div>
          ))}
        </div>
      </CnBalloon>
    </div>
  );
};

export default RichInput;

